import React, { useContext, useMemo, useState } from "react";
import { Button, Modal } from "antd";
import { useTranslation } from "react-i18next";

import { subscribeAddon, unSubscribeAddon } from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { AppContext } from "../../../context/AppContextProvider";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

import { getAddonBlockedReasonKey } from "./addonPresentation";

const AddonAction = ({
  addonData,
  reloadCallback,
  buttonSize = "default",
  variant = "default",
  onActionLoadingChange,
}) => {
  const { authUser, refreshAuthUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState(null); // truthy ⇒ modal open

  const isRtl = appConfig?.direction === "rtl";
  const activeAddons = authUser?.active_addons || [];

  const localizedName = (addon) =>
    isRtl ? addon?.name_ar || addon?.name : addon?.name;

  const isSubscribeBlockedByRule = Boolean(
    getAddonBlockedReasonKey(addonData, activeAddons),
  );

  const missingDependentAddons = useMemo(
    () => (addonData?.dependent_addons || []).filter((a) => !a?.is_subscribed),
    [addonData],
  );

  const closeModal = () => setModalData(null);

  const runAction = ({ apiCall, id, titleKey, messageKey, onSuccess }) => {
    let keepActionLoading = false;
    setLoading(true);
    onActionLoadingChange?.(true);

    apiCall(id)
      .then((response) => {
        const data = response?.data;

        if (data?.success === true) {
          keepActionLoading = Boolean(
            reloadCallback?.({ keepActionLoading: true }),
          );
          NotificationWithIcon("success", t(titleKey), t(messageKey));
          onSuccess?.();
          refreshAuthUser?.();
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
        if (!keepActionLoading) onActionLoadingChange?.(false);
      });
  };

  const handleActivate = (id) => {
    if (missingDependentAddons.length > 0) {
      const addonNames = missingDependentAddons
        .map(localizedName)
        .filter(Boolean)
        .join(", ");

      showAppError(
        addonNames
          ? t("subscribe_dependent_addons", { addons: addonNames })
          : t("something_went_wrong"),
      );
      return;
    }

    runAction({
      apiCall: subscribeAddon,
      id,
      titleKey: "addon_subscribed",
      messageKey: "addon_subscribed_successfully",
    });
  };

  const handleDeActivate = (id) =>
    runAction({
      apiCall: unSubscribeAddon,
      id,
      titleKey: "addon_unsubscribed",
      messageKey: "addon_unsubscribed_successfully",
      onSuccess: closeModal,
    });

  const handleClickUnsubscribe = (data) => {
    const requiredActiveAddons = (data?.required_by_addons || []).filter(
      (req) => activeAddons.some((active) => req?.name === active?.name),
    );
    setModalData({ ...data, required_by_addons: requiredActiveAddons });
  };

  const getActionButtonProps = (isSubscribed) => {
    if (variant === "card" || variant === "modal") {
      return {
        size: variant === "modal" ? "large" : buttonSize,
        block: variant === "modal",
        type: "primary",
      };
    }

    return isSubscribed
      ? { danger: true, type: "primary", size: buttonSize }
      : { type: "primary", size: buttonSize };
  };

  if (!addonData) return null;

  const modalVisible = Boolean(modalData);
  const dependents = modalData?.required_by_addons || [];

  return (
    <>
      {addonData?.is_subscribed ? (
        <Button
          type="text"
          danger
          onClick={() => handleClickUnsubscribe(addonData)}
          disabled={loading}
          {...getActionButtonProps(true)}
        >
          {t("unsubscribe")}
        </Button>
      ) : (
        <Button
          type="primary"
          onClick={() => handleActivate(addonData?.id)}
          disabled={loading || isSubscribeBlockedByRule}
          {...getActionButtonProps(false)}
        >
          {t("subscribe")}
        </Button>
      )}

      <Modal
        open={modalVisible}
        title={t("unsubscribe")}
        className="addon-unsubscribe-modal"
        closable={!loading}
        keyboard={!loading}
        maskClosable={!loading}
        onCancel={() => !loading && closeModal()}
        footer={[
          <Button
            type="default"
            key="cancel"
            disabled={loading}
            onClick={() => !loading && closeModal()}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="unsubscribe"
            type="text"
            danger
            disabled={loading}
            onClick={() => handleDeActivate(modalData?.id)}
          >
            {t("unsubscribe")}
          </Button>,
        ]}
      >
        {dependents.length > 0 ? (
          <>
            <div className="addon-unsubscribe-modal__warning">
              {t("unsubscribe_dependency_warning", {
                addon: localizedName(modalData),
              })}
            </div>
            {dependents.map((addon) => (
              <div
                className="addon-unsubscribe-modal__addon-row"
                key={addon?.id}
              >
                {localizedName(addon)}
              </div>
            ))}
          </>
        ) : (
          <div className="addon-unsubscribe-modal__warning">
            {t("warning_unsubscribe_addon")}
          </div>
        )}
      </Modal>
    </>
  );
};

export default AddonAction;
